// ────────────────────────────────────────────────────────────
// DEAL DESK — DERIVE: raw D1 rows → TractBundle + thin DealRecords
// ────────────────────────────────────────────────────────────
// THE TRACT OWNS THE TITLE WORK. buildTractBundle() derives the
// chain, rail, untraced branches, parties, worksheet, and flags —
// subject-agnostic. deriveDealRecords() maps deals rows to thin
// records. dealLens() computes one deal's subject figures against
// its tract. Chain data is never copied onto deals.
//
// CHAIN MATH IS DERIVED, NEVER STORED:
//   · severance factors parse from the verbatim RESERVED clause
//   · the continuing side of a severance is whichever party appears
//     again downstream; the other party becomes an UNTRACED BRANCH
//   · party figures = invariant estate fractions × the computed rail
//     at the fracture (per-heir probate shares aren't in deed text,
//     so fractions are backed out of the agent's disposition once —
//     shares survive upstream changes; acres always recompute)
//   · TENTATIVE (pending) severances never move numbers — shadow only
// Stored numbers survive only as cross-checks (mathNotes).

import type {
  TractBundle, ChainNode, FanGrantee, FlagItem, FlagGroup, TractParty,
  WorksheetRow, LinkStatus, RailDrop, UntracedBranch, RevisionEntry,
  DealRecord, DealLens, EstateStep, SubjectTrackStep,
} from '../types';
import type { DealRows, ChainLinkRow } from './rows';

// ─── identity keys ──────────────────────────────────────────

/**
 * "JERRIE L. WHITE" → "JERRIE WHITE": first+last alpha token. The
 * view-side join key for highlighting and party matching. Exported
 * so pages can match a deal's subject against tract chain parties.
 */
export function partyMatchKey(name: string): string {
  const tokens = name.replace(/[^A-Za-z\s]/g, ' ').split(/\s+/).filter(t => t.length > 1);
  if (tokens.length === 0) return name.toUpperCase().trim();
  if (tokens.length === 1) return tokens[0].toUpperCase();
  return `${tokens[0]} ${tokens[tokens.length - 1]}`.toUpperCase();
}

/** Split a party string on alias markers: "A, also known as B, aka C" → [A, B, C]. */
export function aliasVariants(name: string): string[] {
  return name
    .split(/,?\s*(?:also known as|a\/k\/a|aka)\s+/i)
    .map(s => s.trim())
    .filter(Boolean);
}

/** Display name for a verbatim party: text up to the first comma. */
export function shortName(raw: string): string {
  const cut = raw.split(',')[0].trim();
  return cut || raw.trim();
}

function partyKeys(raw: string): string[] {
  return aliasVariants(raw).map(v => partyMatchKey(shortName(v)));
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const atWord = cut.lastIndexOf(' ');
  return `${cut.slice(0, atWord > max * 0.6 ? atWord : max).trimEnd()}…`;
}

function yearOf(iso: string): number {
  return parseInt(iso.slice(0, 4), 10);
}

function daysBetween(isoA: string, isoB: string): number {
  return Math.abs(new Date(isoA).getTime() - new Date(isoB).getTime()) / 86_400_000;
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

// ─── fraction parsing from verbatim language ────────────────

const WORD_FRACTIONS: Array<[RegExp, number, string]> = [
  [/\bone[-\s]half\b|½/i, 1 / 2, '1/2'],
  [/\bthree[-\s]quarters?\b|¾/i, 3 / 4, '3/4'],
  [/\bone[-\s](?:quarter|fourth)\b|¼/i, 1 / 4, '1/4'],
  [/\btwo[-\s]thirds?\b/i, 2 / 3, '2/3'],
  [/\bone[-\s]third\b/i, 1 / 3, '1/3'],
  [/\bone[-\s]eighth\b/i, 1 / 8, '1/8'],
  [/\bone[-\s]sixteenth\b/i, 1 / 16, '1/16'],
];

export function fractionFromText(text: string): { value: number; label: string } | null {
  const numeric = text.match(/\b(\d+)\s*\/\s*(\d+)\b/);
  if (numeric) {
    const n = parseInt(numeric[1], 10);
    const d = parseInt(numeric[2], 10);
    if (d > 0 && n > 0 && n < d) return { value: n / d, label: `${n}/${d}` };
  }
  for (const [re, value, label] of WORD_FRACTIONS) {
    if (re.test(text)) return { value, label };
  }
  return null;
}

function parseFractionLabel(label: string): number | null {
  const m = label.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (!m) return null;
  const n = parseInt(m[1], 10), d = parseInt(m[2], 10);
  return d > 0 ? n / d : null;
}

// ─── chain-link field parsing ───────────────────────────────

interface ParsedDetail {
  interestSummary?: string;
  reservedQuote?: string;
  grantingQuote?: string;
}

function parseDetail(detail: string | null): ParsedDetail {
  const out: ParsedDetail = {};
  if (!detail) return out;
  for (const seg of detail.split(' · ')) {
    const s = seg.trim();
    let m: RegExpMatchArray | null;
    if ((m = s.match(/^interest:\s*(.+)$/s))) {
      out.interestSummary = m[1].trim();
    } else if ((m = s.match(/^RESERVED\s*—\s*"?(.+?)"?\s*$/s))) {
      out.reservedQuote = m[1].trim();
    } else if ((m = s.match(/^granting:\s*"?(.+?)"?\s*$/s))) {
      out.grantingQuote = m[1].trim();
    }
  }
  return out;
}

function parseConveyance(conveyance: string): { grantor: string; grantees: string[] } {
  const clean = conveyance.replace(/\*\*/g, '');
  const arrowIdx = clean.indexOf('→');
  if (arrowIdx < 0) return { grantor: clean.trim(), grantees: [] };
  const grantor = clean.slice(0, arrowIdx).trim();
  const grantees = clean.slice(arrowIdx + 1).split(';').map(s => s.trim()).filter(Boolean);
  return { grantor, grantees };
}

function distributionDecedent(grantor: string): string {
  const estate = grantor.match(/Estate of\s+([^,]+)/i);
  if (estate) return estate[1].trim();
  return shortName(grantor);
}

// ─── parsed link (pre-engine) ───────────────────────────────

interface ParsedLink {
  row: ChainLinkRow;
  grantor: string;
  grantees: string[];
  grantorKeys: string[];
  granteeKeys: string[];
  detail: ParsedDetail;
  severance: boolean;
  notApplied: boolean;
  isDistribution: boolean;
  status: LinkStatus;
  applied: boolean;
}

function parseLinks(rows: DealRows, notAppliedDocs: Set<string>): ParsedLink[] {
  const sorted = [...rows.chain_links].sort((a, b) => a.ordinal - b.ordinal);
  return sorted.map(row => {
    const { grantor, grantees } = parseConveyance(row.conveyance);
    const detail = parseDetail(row.detail);
    const status = (['verified', 'flagged', 'pending', 'current'].includes(row.status)
      ? row.status : 'pending') as LinkStatus;
    const notApplied = !!row.doc_number && notAppliedDocs.has(row.doc_number);
    return {
      row,
      grantor,
      grantees,
      grantorKeys: partyKeys(grantor),
      granteeKeys: grantees.flatMap(partyKeys),
      detail,
      severance: !!detail.reservedQuote,
      notApplied,
      isDistribution:
        /Personal Representative|Decree of Heirship|Distribution/i.test(row.doc_type) ||
        grantees.length >= 4,
      status,
      // TENTATIVE (pending) never moves numbers; flagged is applied-with-open-question.
      applied: status !== 'pending' && !notApplied,
    };
  });
}

// ─── the chain-math engine ──────────────────────────────────

export interface ChainMath {
  baseAcres: number;
  railAt: Map<string, number | null>;
  dropAt: Map<string, RailDrop>;
  shadowAt: Map<string, { factor: string; multiplier: number }>;
  estateAcres: number;
  fractureLinkId: string | null;
  untraced: UntracedBranch[];
  notes: string[];
}

export function computeChainMath(links: ParsedLink[], baseAcres: number): ChainMath {
  const railAt = new Map<string, number | null>();
  const dropAt = new Map<string, RailDrop>();
  const shadowAt = new Map<string, { factor: string; multiplier: number }>();
  const untraced: UntracedBranch[] = [];
  const notes: string[] = [];

  const lastSeen = new Map<string, number>();
  links.forEach((l, i) => {
    for (const k of [...l.grantorKeys, ...l.granteeKeys]) lastSeen.set(k, i);
  });
  const appearsAfter = (keys: string[], i: number) =>
    keys.some(k => (lastSeen.get(k) ?? -1) > i);

  let rail: number | null = baseAcres;
  let fractured = false;
  let estateAcres = baseAcres;
  let fractureLinkId: string | null = null;

  links.forEach((l, i) => {
    railAt.set(l.row.id, fractured ? null : rail);

    if (l.severance && rail !== null && !fractured) {
      const frac = l.detail.reservedQuote ? fractionFromText(l.detail.reservedQuote) : null;
      if (!frac) {
        notes.push(`Could not parse the reserved fraction on ${l.row.doc_number ?? l.row.id}; rail unchanged there.`);
      } else if (!l.applied) {
        shadowAt.set(l.row.id, { factor: frac.label, multiplier: 1 - frac.value });
      } else {
        const grantorContinues = appearsAfter(l.grantorKeys, i);
        const granteeContinues = appearsAfter(l.granteeKeys, i);
        const before = rail;
        let after: number;
        let head: { name: string; side: 'reserved' | 'received' } | null = null;

        if (granteeContinues && !grantorContinues) {
          after = before * (1 - frac.value);
          head = { name: shortName(l.grantor), side: 'reserved' };
        } else if (grantorContinues && !granteeContinues) {
          after = before * frac.value;
          head = { name: shortName(l.grantees[0] ?? l.grantor), side: 'received' };
        } else {
          after = before * (1 - frac.value);
          if (!grantorContinues && !granteeContinues) {
            notes.push(`Neither party of severance ${l.row.doc_number} appears downstream; treated reserver as the departing branch.`);
            head = { name: shortName(l.grantor), side: 'reserved' };
          }
        }

        dropAt.set(l.row.id, {
          docNumber: l.row.doc_number ?? '?',
          factor: frac.label,
          before,
          after,
        });
        if (head) {
          untraced.push({
            name: head.name,
            matchKey: partyMatchKey(head.name),
            acres: before - after,
            sinceYear: yearOf(l.row.date),
            docNumber: l.row.doc_number ?? undefined,
            side: head.side,
          });
        }
        rail = after;
        railAt.set(l.row.id, before);
      }
    }

    if (l.isDistribution && l.applied && !fractured) {
      fractured = true;
      estateAcres = rail ?? baseAcres;
      fractureLinkId = l.row.id;
    }
  });

  if (!fractured) estateAcres = rail ?? baseAcres;

  return { baseAcres, railAt, dropAt, shadowAt, estateAcres, fractureLinkId, untraced, notes };
}

// ─── flags ──────────────────────────────────────────────────

function classifyFlag(text: string): FlagGroup {
  if (/never connected to a held interest/i.test(text)) return 'gap';
  if (/OPEN QUESTION/i.test(text)) return 'pricing';
  if (/CHAIN INCOMPLETE|CONFIDENCE CAPPED|no patent record/i.test(text)) return 'critical';
  if (/R2|mirroring|blob/i.test(text)) return 'ops';
  return 'other';
}

const FLAG_GROUP_ORDER: FlagGroup[] = ['critical', 'pricing', 'gap', 'ops', 'other'];

// ─── stored disposition parsing ─────────────────────────────

interface StoredOwner { name: string; nma: number; conf: string }

function parseStoredOwners(position: string): StoredOwner[] {
  const owners: StoredOwner[] = [];
  for (const line of position.split('\n')) {
    const m = line.match(/^-\s*(.+?)\s+—\s+([\d.]+)\s+NMA\s+\((\w+)\)/);
    if (!m) {
      if (/\*\*Open questions/i.test(line)) break;
      continue;
    }
    owners.push({ name: m[1].trim(), nma: parseFloat(m[2]), conf: m[3] });
  }
  return owners;
}

// ─── baseline fractions ─────────────────────────────────────

export interface OwnerFractions {
  byKey: Map<string, number>;
  subjectShareLabel?: string;
}

/**
 * Back out invariant estate fractions from the agent's stored disposition
 * against a BASELINE chain-math run. Shares survive upstream changes;
 * acres never come from storage.
 */
export function deriveOwnerFractions(rows: DealRows): OwnerFractions {
  const notAppliedDocs = collectNotApplied(rows);
  const links = parseLinks(rows, notAppliedDocs);
  const worksheetRows = parseWorksheetRows(rows);
  const base = parseBaseAcres(worksheetRows);
  const math = computeChainMath(links, base);

  const theory = parseTheory(rows);
  const stored = parseStoredOwners(theory.position);
  const untracedKeys = new Set(math.untraced.map(u => u.matchKey));

  const byKey = new Map<string, number>();
  for (const o of stored) {
    const key = partyMatchKey(o.name);
    if (untracedKeys.has(key)) continue;
    if (math.estateAcres > 0) byKey.set(key, o.nma / math.estateAcres);
  }

  // The worksheet's exact stated share ("1/45") wins over back-derivation
  // for the party it names (the agent's subject at title time).
  let subjectShareLabel: string | undefined;
  for (const r of worksheetRows) {
    const m = r.label.match(/share of the estate:\s*([\d/]+)/i);
    if (m) subjectShareLabel = m[1];
  }
  if (subjectShareLabel) {
    const exact = parseFractionLabel(subjectShareLabel);
    const subjectKey = partyMatchKey(theory.subjectParty);
    if (exact !== null) byKey.set(subjectKey, exact);
  }

  return { byKey, subjectShareLabel };
}

// ─── shared row parsing ─────────────────────────────────────

function collectNotApplied(rows: DealRows): Set<string> {
  const set = new Set<string>();
  for (const f of rows.flags) {
    if (f.resolved) continue;
    if (/never connected to a held interest/i.test(f.text)) {
      const m = f.text.match(/event (\d+)/);
      if (m) set.add(m[1]);
    }
  }
  return set;
}

function parseWorksheetRows(rows: DealRows): WorksheetRow[] {
  const mathRow = rows.math_worksheets[0];
  try { return mathRow ? JSON.parse(mathRow.rows_json) : []; } catch { return []; }
}

function parseBaseAcres(worksheetRows: WorksheetRow[]): number {
  for (const r of worksheetRows) {
    const m = r.value.match(/^([\d,]+(?:\.\d+)?)\s*ac\b/);
    if (r.kind === 'normal' && m && !/^[×x]/.test(r.label.trim())) {
      return parseFloat(m[1].replace(/,/g, ''));
    }
  }
  return 0;
}

function parseTheory(rows: DealRows): {
  position: string; subjectParty: string; terminatesAtPatent: boolean; confidence: number;
} {
  const theoryRow = rows.working_theories[0];
  let fields: Record<string, unknown> = {};
  try { fields = theoryRow ? JSON.parse(theoryRow.fields_json) : {}; } catch { /* keep {} */ }
  return {
    position: String(fields.position ?? ''),
    subjectParty: String(fields.subjectParty ?? rows.deal[0]?.owner_name ?? ''),
    terminatesAtPatent: fields.terminatesAtPatent === true,
    confidence: theoryRow?.confidence ?? 0,
  };
}

// ─── TRACT bundle ───────────────────────────────────────────

const YEAR_GAP_THRESHOLD = 8;
const DUP_WINDOW_DAYS = 45;

export function buildTractBundle(rows: DealRows, baseline?: OwnerFractions): TractBundle {
  const deal = rows.deal[0];
  if (!deal) throw new Error('No deal row in dump');

  const theory = parseTheory(rows);
  const worksheetRows = parseWorksheetRows(rows);
  const baseAcres = parseBaseAcres(worksheetRows);
  const notAppliedDocs = collectNotApplied(rows);
  const links = parseLinks(rows, notAppliedDocs);

  const math = computeChainMath(links, baseAcres);
  const fractions = baseline ?? deriveOwnerFractions(rows);

  const flags: FlagItem[] = rows.flags
    .filter(f => !f.resolved)
    .map(f => {
      const group = classifyFlag(f.text);
      return {
        id: f.id,
        text: f.text.replace(/%%/g, '%'),
        group,
        linkedDocNumber: group === 'gap' ? f.text.match(/event (\d+)/)?.[1] : undefined,
      };
    })
    .sort((a, b) => FLAG_GROUP_ORDER.indexOf(a.group) - FLAG_GROUP_ORDER.indexOf(b.group));

  const competitorDocs = new Set(
    rows.findings
      .filter(f => /^COMPETITOR PURCHASE/i.test(f.detail))
      .map(f => f.doc_number)
      .filter((d): d is string => !!d),
  );

  const filesById = new Map(rows.files.map(f => [f.id, f]));

  // Chain assembly (subject-agnostic — highlighting is a view concern).
  const heirRegistry = new Map<string, string>();
  let fractured = false;
  const chain: ChainNode[] = links.map((l, i) => {
    const link = l.row;

    let branchOwner: string | undefined;
    if (fractured || l.isDistribution) {
      for (const v of aliasVariants(l.grantor)) {
        const hit = heirRegistry.get(partyMatchKey(shortName(v)));
        if (hit) { branchOwner = hit; break; }
      }
    }

    let fan: FanGrantee[] | undefined;
    let distributionOf: string | undefined;
    if (l.isDistribution) {
      distributionOf = distributionDecedent(l.grantor);
      fan = l.grantees.map(g => {
        const label = shortName(aliasVariants(g)[0] ?? g);
        if (!l.notApplied) {
          for (const v of aliasVariants(g)) heirRegistry.set(partyMatchKey(shortName(v)), label);
        }
        return { label, full: g, matchKey: partyMatchKey(label) };
      });
    }
    if (l.isDistribution && l.applied) fractured = true;

    let dupNote: string | undefined;
    for (let j = i - 1; j >= 0; j--) {
      const prev = links[j];
      if (daysBetween(prev.row.date, link.date) > DUP_WINDOW_DAYS) break;
      const sameParties =
        prev.grantorKeys.some(k => l.grantorKeys.includes(k)) &&
        prev.granteeKeys.some(k => l.granteeKeys.includes(k));
      if (sameParties && prev.row.doc_number !== link.doc_number) {
        const days = Math.round(daysBetween(prev.row.date, link.date));
        dupNote = `Duplicate or corrective deed? Same parties as ${prev.row.doc_number}, ${days} day${days === 1 ? '' : 's'} earlier.`;
        break;
      }
    }

    const gapYears = i > 0 ? yearOf(link.date) - yearOf(links[i - 1].row.date) : 0;
    const rawShadow = math.shadowAt.get(link.id);
    const competitorPurchase = !l.isDistribution && !!link.doc_number && competitorDocs.has(link.doc_number);
    const file = link.file_id ? filesById.get(link.file_id) : undefined;

    return {
      id: link.id,
      ordinal: link.ordinal,
      docNumber: link.doc_number ?? undefined,
      docType: link.doc_type,
      date: link.date,
      grantor: l.grantor,
      grantees: l.grantees,
      grantorMatchKeys: l.grantorKeys,
      granteeMatchKeys: l.granteeKeys,
      interestSummary: l.detail.interestSummary,
      reservedQuote: l.detail.reservedQuote,
      grantingQuote: l.detail.grantingQuote,
      status: l.status,
      notApplied: l.notApplied,
      severance: l.severance,
      railDrop: math.dropAt.get(link.id),
      shadow: rawShadow ? { factor: rawShadow.factor, multiplier: rawShadow.multiplier } : undefined,
      railAcres: math.railAt.get(link.id) ?? null,
      isDistribution: l.isDistribution,
      distributionOf,
      fan,
      branchOwner,
      competitorPurchase,
      buyerName: competitorPurchase ? shortName(l.grantees[0] ?? '') : undefined,
      gapYearsBefore: gapYears >= YEAR_GAP_THRESHOLD ? gapYears : undefined,
      dupNote,
      fileId: link.file_id ?? undefined,
      fileName: file?.name,
    };
  });

  // Docs where each party key appears (feeds skip-trace payloads + expand).
  const docsByKey = new Map<string, Set<string>>();
  for (const l of links) {
    if (!l.row.doc_number) continue;
    for (const k of [...l.grantorKeys, ...l.granteeKeys]) {
      if (!docsByKey.has(k)) docsByKey.set(k, new Set());
      docsByKey.get(k)!.add(l.row.doc_number);
    }
  }

  const stored = parseStoredOwners(theory.position);
  const storedByKey = new Map(stored.map(o => [partyMatchKey(o.name), o]));
  const untracedKeys = new Set(math.untraced.map(u => u.matchKey));
  const mathNotes: string[] = [...math.notes];

  // Conveyance-out index for disposition status. A party who is the grantor
  // of a non-distribution deed conveyed their interest OUT. Estate
  // distributions are excluded: they are executed by a PR, not the heir
  // conveying their own share — so signing one does not mean the heir sold.
  const soldByKey = new Map<string, { to: string; doc?: string }>();
  for (const n of chain) {
    if (n.isDistribution || n.grantees.length === 0) continue;
    const to = n.buyerName ?? shortName(n.grantees[0]);
    for (const k of n.grantorMatchKeys) {
      if (!soldByKey.has(k)) soldByKey.set(k, { to, doc: n.docNumber });
    }
  }
  const findSold = (key: string): { to: string; doc?: string } | undefined => {
    const direct = soldByKey.get(key);
    if (direct) return direct;
    // Compound-name fallback (e.g. long trustee strings the key can't index).
    for (const n of chain) {
      if (n.isDistribution || n.grantees.length === 0) continue;
      if (matchesPartyText(n.grantor, key)) return { to: n.buyerName ?? shortName(n.grantees[0]), doc: n.docNumber };
    }
    return undefined;
  };

  const parties: TractParty[] = stored
    .filter(o => !untracedKeys.has(partyMatchKey(o.name)))
    .map(o => {
      const key = partyMatchKey(o.name);
      const fraction = fractions.byKey.get(key) ?? (math.estateAcres > 0 ? o.nma / math.estateAcres : 0);
      const sold = findSold(key);
      return {
        name: o.name,
        matchKey: key,
        nma: round4(fraction * math.estateAcres),
        fraction,
        conf: o.conf,
        kind: (/\b(LLC|L\.L\.C\.|FUND|MINERALS|INC|CORP)\b/i.test(o.name) ? 'competitor' : 'individual') as TractParty['kind'],
        share: math.baseAcres > 0 ? (fraction * math.estateAcres) / math.baseAcres : 0,
        chainDocNumbers: [...(docsByKey.get(key) ?? [])],
        disposition: (sold ? 'sold' : 'holds') as TractParty['disposition'],
        soldTo: sold?.to,
        soldDoc: sold?.doc,
      };
    })
    .sort((a, b) => b.nma - a.nma);

  const untraced: UntracedBranch[] = math.untraced.map(u => {
    const storedRow = storedByKey.get(u.matchKey);
    if (storedRow && Math.abs(storedRow.nma - u.acres) > 0.01) {
      mathNotes.push(
        `Untraced branch ${u.name}: derived ${u.acres.toFixed(4)} ac disagrees with the stored disposition ${storedRow.nma.toFixed(4)} ac.`,
      );
    }
    return { ...u, storedAcres: storedRow?.nma };
  });

  const untracedTotal = round4(untraced.reduce((a, u) => a + u.acres, 0));
  const tracedTotal = round4(parties.reduce((a, o) => a + o.nma, 0));

  const confSet = new Set(stored.map(o => o.conf));
  const confUniform = confSet.size === 1 ? [...confSet][0] : null;

  const confidenceReasons = flags
    .filter(f => f.group === 'critical' || f.group === 'pricing')
    .slice(0, 3)
    .map(f => truncate(f.text, 120));

  const openEndNote =
    flags.find(f => /no patent record/i.test(f.text))?.text
    ?? 'Chain does not terminate at a patent or sovereign conveyance.';

  const revisions: RevisionEntry[] = (rows.revision_log ?? []).map(r => ({
    id: r.id,
    docNumber: r.doc_number,
    occurredAt: r.occurred_at,
    field: r.field,
    oldValue: r.old_value,
    newValue: r.new_value,
    reason: r.reason ?? undefined,
  }));

  return {
    trs: deal.trs,
    county: deal.county,
    statusLine: deal.status ?? '',
    titledAt: deal.created_at,
    confidence: theory.confidence,
    confidenceReasons,
    terminatesAtPatent: theory.terminatesAtPatent,
    openEndNote,
    grossAcres: math.baseAcres,
    estateAcres: math.estateAcres,
    chain,
    worksheet: {
      rows: worksheetRows,
      result: rows.math_worksheets[0]?.result ?? '',
      reconciliation: rows.math_worksheets[0]?.reconciliation ?? undefined,
    },
    flags,
    parties,
    untraced,
    untracedTotal,
    tracedTotal,
    confUniform,
    mathNotes,
    revisions,
    subjectShareLabel: fractions.subjectShareLabel,
  };
}

// ─── thin DEAL records + the deal lens ──────────────────────

export function deriveDealRecords(rows: DealRows): DealRecord[] {
  return rows.deal.map(d => ({
    id: d.id,
    ref: d.ref,
    trs: d.trs,
    county: d.county,
    subjectParty: d.owner_name,
    stage: d.stage,
    status: d.status ?? '',
    closeUrl: d.close_id ? `https://app.close.com/lead/${d.close_id}/` : null,
    createdAt: d.created_at,
    closedOutcome: (d as any).closed_outcome ?? undefined,
    soldTo: (d as any).sold_to ?? undefined,
    closedAt: (d as any).closed_at ?? undefined,
  }));
}

/**
 * Loose text match for compound party strings ("Daniel P. McCartney and
 * Violet M. McCartney as Joint Tenants") where first+last-token keys
 * mangle. True when every token of the key appears in the raw text.
 */
export function matchesPartyText(raw: string, key: string): boolean {
  const up = raw.toUpperCase();
  return key.split(' ').every(t => up.includes(t));
}

/**
 * Walk interest carriers BACKWARD from the subject to find the spine —
 * the links the priced interest actually passed through. At each hit,
 * the upstream holder joins the carrier set. Not-applied side events
 * are never spine; everything off the spine is sibling-branch context.
 *
 * Two kinds of names come out of the walk, and they are different
 * title facts:
 *   · interest carriers — held the interest and passed it (grantees
 *     who took, decedents whose estates distributed)
 *   · executors — the PR named in a distribution's grantor string;
 *     they SIGNED the conveyance, no interest passed through them
 *     personally. Excluded from walk matching so an executor's own
 *     unrelated dispositions can never false-match onto the spine.
 */
function computeSpine(chain: ChainNode[], subjectKey: string): {
  spineLinkIds: string[]; carrierKeys: string[]; executorKeys: string[];
} {
  const carriers = new Set<string>([subjectKey]);   // interest carriers only
  const executors = new Set<string>();
  const spine = new Set<string>();

  const anyCarrier = (keys: string[], texts: string[]): boolean => {
    if (keys.some(k => carriers.has(k))) return true;
    for (const c of carriers) {
      if (texts.some(t => matchesPartyText(t, c))) return true;
    }
    return false;
  };

  for (let i = chain.length - 1; i >= 0; i--) {
    const n = chain[i];
    if (n.notApplied) continue;

    const granteeHit = anyCarrier(n.granteeMatchKeys, n.grantees);
    const grantorHit = anyCarrier(n.grantorMatchKeys, [n.grantor]);

    if (granteeHit) {
      spine.add(n.id);
      if (n.isDistribution && n.distributionOf) {
        // The DECEDENT held the interest; the PR who executed the deed
        // did not. Grantor keys that aren't the decedent are executors.
        const decedentKey = partyMatchKey(n.distributionOf);
        carriers.add(decedentKey);
        for (const k of n.grantorMatchKeys) {
          if (k !== decedentKey && !matchesPartyText(n.distributionOf, k)) executors.add(k);
        }
      } else {
        // Plain conveyance: the grantor held and passed.
        for (const k of n.grantorMatchKeys) carriers.add(k);
      }
    } else if (grantorHit) {
      // A carrier acted as grantor — the retained side of a severance
      // (or the carrier's own disposition). The interest passed through.
      spine.add(n.id);
    }
  }

  // A name can be both (PR of one estate, heir of another). Interest
  // carriage is the stronger fact; it wins.
  for (const k of carriers) executors.delete(k);

  return { spineLinkIds: [...spine], carrierKeys: [...carriers], executorKeys: [...executors] };
}

/**
 * Subject figures for one deal, computed against its tract. The deal
 * stores nothing acreage-shaped; nma_estimate survives only as a
 * cross-check input the caller may pass.
 */
export function dealLens(tract: TractBundle, subjectParty: string, nmaStored: number | null): DealLens {
  const highlightKey = partyMatchKey(subjectParty);
  const party = tract.parties.find(p => p.matchKey === highlightKey);
  const subjectFraction = party?.fraction ?? null;
  const nmaComputed = subjectFraction !== null ? round4(subjectFraction * tract.estateAcres) : null;
  const { spineLinkIds, carrierKeys, executorKeys } = computeSpine(tract.chain, highlightKey);
  const estatePath = computeEstatePath(tract.chain, highlightKey);
  const subjectTrack = computeSubjectTrack(
    estatePath, tract.estateAcres, tract.subjectShareLabel, nmaComputed,
  );
  return {
    subjectFraction, nmaComputed, nmaStored, highlightKey,
    spineLinkIds, carrierKeys, executorKeys, estatePath, subjectTrack,
  };
}

/** Express a decimal as the simplest small-denominator fraction, or null. */
function decimalToFraction(value: number): string | null {
  if (!(value > 0) || value >= 1) return null;
  for (let d = 2; d <= 64; d++) {
    const n = Math.round(value * d);
    if (n >= 1 && Math.abs(n / d - value) < 0.0015 && gcd(n, d) === 1) return `${n}/${d}`;
  }
  return null;
}
function gcd(a: number, b: number): number { return b === 0 ? a : gcd(b, a % b); }

/**
 * The subject's running interest as it narrows through each estate
 * fracture — DERIVED FROM THE WORKSHEET, never recomputed independently,
 * so the track can never disagree with the verified worksheet:
 *   · the estate base (40 ac) and the composite subject share (1/45 →
 *     0.8889) come straight from the math_worksheet;
 *   · each distribution the subject is a named grantee of takes an equal
 *     1-of-N share (countable from the chain), applied at that link;
 *   · the ROOT distribution absorbs whatever residual makes the running
 *     product equal the worksheet composite exactly.
 * White: 40 → ×1/9 → 4.4444 (Violet 3994346) → ×1/5 → 0.8889 (Arlene
 * 3997026, Robert is 1 of 5). Endpoints and product are the worksheet's.
 */
function computeSubjectTrack(
  estatePath: EstateStep[],
  estateAcres: number,
  subjectShareLabel: string | undefined,
  subjectFinal: number | null,
): SubjectTrackStep[] {
  if (estatePath.length === 0 || subjectFinal === null || !subjectShareLabel) return [];
  const composite = parseFractionLabel(subjectShareLabel);   // 1/45 → 0.02222
  if (composite === null || !(estateAcres > 0)) return [];

  const n = estatePath.length;
  const fractions: number[] = new Array(n).fill(1);
  // Non-root fractures: the subject's line takes an equal 1-of-N grantee
  // share at each (the fraction renders at that fracture's own link).
  let nonRootProduct = 1;
  for (let i = 1; i < n; i++) {
    const gc = estatePath[i].granteeCount;
    fractions[i] = gc > 0 ? 1 / gc : 1;
    nonRootProduct *= fractions[i];
  }
  // Root fracture absorbs the residual so the product = the worksheet composite.
  fractions[0] = nonRootProduct > 0 ? composite / nonRootProduct : composite;

  const valid = fractions.every(f => f > 0 && f <= 1);

  const steps: SubjectTrackStep[] = [];
  let before = estateAcres;
  for (let i = 0; i < n; i++) {
    const isLast = i === n - 1;
    // Pin the final value to the worksheet's subject NMA (no rounding drift).
    const after = isLast ? round4(subjectFinal) : round4(before * fractions[i]);
    steps.push({
      docNumber: estatePath[i].docNumber,
      decedent: estatePath[i].decedent,
      before: round4(before),
      after,
      fractionLabel: valid ? decimalToFraction(fractions[i]) ?? undefined : undefined,
      clean: valid,
    });
    before = after;
  }
  return steps;
}

/**
 * The estate distributions the subject's interest passes through, root→
 * subject. Found by locating the deepest distribution the subject takes
 * from, then walking UP: a parent distribution is the one whose grantees
 * include the child's decedent (that estate took a share and then
 * distributed it further). Makes a composite estate share like "1/45"
 * legible as a doc-cited sequence of fractures.
 */
function computeEstatePath(chain: ChainNode[], subjectKey: string): EstateStep[] {
  const dists = chain.filter(n => n.isDistribution && !n.notApplied && n.fan && n.fan.length > 0);
  if (dists.length === 0) return [];

  const takesSubject = (n: ChainNode) =>
    !!n.fan?.some(g => g.matchKey === subjectKey || matchesPartyText(g.full, subjectKey));
  const subjectDist = [...dists].reverse().find(takesSubject);
  if (!subjectDist) return [];

  const path: ChainNode[] = [subjectDist];
  const used = new Set([subjectDist.id]);
  let current = subjectDist;
  while (current.distributionOf) {
    const decKey = partyMatchKey(current.distributionOf);
    const parent = dists.find(d => !used.has(d.id) && d.fan!.some(g => matchesPartyText(g.full, decKey)));
    if (!parent) break;
    path.unshift(parent);
    used.add(parent.id);
    current = parent;
  }

  return path.map(d => ({
    docNumber: d.docNumber,
    decedent: d.distributionOf ?? 'unknown estate',
    granteeCount: d.fan!.length,
    subjectIsGrantee: d.id === subjectDist.id,
  }));
}
